import { useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import type { CustomerEmailLogRow } from './types';

const PAGE_SIZE = 10;

/**
 * Render a logged email body as readable text.
 *
 * Bodies are stored as the HTML that was actually sent, so showing the raw
 * column puts `<table>` and inline styles on the screen. This is deliberately a
 * TEXT extraction rather than dangerouslySetInnerHTML or an iframe: the row is
 * attacker-influenced (a customer name, a rejection reason and a job
 * description all reach the template), and nothing here needs the layout — the
 * user is checking what they said, not previewing the design.
 */
function emailBodyToText(body: string): string {
    const withBreaks = body
        .replace(/<\s*(br|\/p|\/div|\/tr|\/h[1-6])\s*\/?>/gi, '\n')
        .replace(/<\s*\/?\s*(style|script)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
    return (doc.body.textContent ?? '')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

interface CommunicationTabProps {
    emails: CustomerEmailLogRow[];
    hasEmail: boolean;
    onOpenEmail: () => void;
}

export function CommunicationTab({ emails, hasEmail, onOpenEmail }: CommunicationTabProps) {
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [openId, setOpenId] = useState<string | null>(null);
    const visible = emails.slice(0, visibleCount);

    return (
        <section className="bg-panel border border-line rounded-xl overflow-hidden">
            <div className="p-4 border-b border-line flex items-center justify-between bg-raised">
                <h3 className="font-bold text-fg flex items-center gap-2">
                    Communication
                </h3>
                {hasEmail && (
                    <button
                        onClick={onOpenEmail}
                        className="text-xs fd-btn-primary px-3 py-1.5 rounded-md font-medium transition-opacity flex items-center gap-1"
                    >
                        <Mail className="w-3.5 h-3.5" />
                        New Email
                    </button>
                )}
            </div>
            <div className="divide-y divide-tint/5">
                {emails.length === 0 ? (
                    <div className="p-8 text-center text-fg-4 text-sm">
                        Nothing sent to this customer yet. Emails you send from ForgeDash are logged here.
                    </div>
                ) : (
                    visible.map((mail) => (
                        <div key={mail.id} className="p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-xs text-fg-3">
                                        <Calendar className="w-3.5 h-3.5 text-fg-4" />
                                        {new Date(mail.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        <span className="text-fg-4 truncate">{mail.recipient}</span>
                                    </div>
                                    <h4 className="font-bold text-fg mt-1 truncate">{mail.subject || '(no subject)'}</h4>
                                    {mail.body && openId !== mail.id && (
                                        <p className="text-sm text-fg-3 line-clamp-2 mt-0.5 whitespace-pre-wrap">{emailBodyToText(mail.body)}</p>
                                    )}
                                    {mail.body && (
                                        <>
                                            <button
                                                onClick={() => setOpenId((cur) => (cur === mail.id ? null : mail.id))}
                                                className="mt-2 inline-flex items-center gap-1 min-h-[44px] text-xs font-bold text-accent hover:underline"
                                                aria-expanded={openId === mail.id}
                                            >
                                                {openId === mail.id ? (
                                                    <>Hide message <ChevronUp className="w-3.5 h-3.5" /></>
                                                ) : (
                                                    <>Read message <ChevronDown className="w-3.5 h-3.5" /></>
                                                )}
                                            </button>
                                            {openId === mail.id && (
                                                <div className="mt-2 rounded-lg border border-tint/10 bg-raised p-3">
                                                    <p className="text-sm text-fg-2 whitespace-pre-wrap break-words">
                                                        {emailBodyToText(mail.body)}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <span className={`shrink-0 text-fd-3xs font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                    mail.status === 'failed'
                                        ? 'bg-danger/10 text-danger'
                                        : 'bg-success/10 text-success'
                                }`}>
                                    {mail.status}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {emails.length > visibleCount && (
                <button
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="w-full p-3 text-xs font-bold text-accent hover:bg-tint/5 transition-colors border-t border-tint/5"
                >
                    Load more ({emails.length - visibleCount} remaining)
                </button>
            )}
        </section>
    );
}
